import { useCallback, useEffect, useState, useMemo } from "react";
import Contacts from "./pages/Contacts";
import Kanban from "./pages/Kanban";
import Conversation from "./pages/Conversation";
import Dashboard from "./pages/Dashboard";
import Deals from "./pages/Deals";
import Accounts from "./pages/Accounts";
import ContactDetail from "./pages/contacts/[id]";
import { getContacts, getFollowUps } from "./services/contact_service";
import useAuthStore from "../states/authStore";
import useDealStore from "./states/useDealStore";
import useAppDataStore from "../states/useAppDataStore";
import { NavBar, PageHeader, PageHeaderProvider, AppSwitcher } from "@iq/models";
import { IoIosStats } from "react-icons/io";
import { FiBriefcase, FiUsers } from "react-icons/fi";
import { TbLayoutDashboard } from "react-icons/tb";
import { BiMessageSquareDots } from "react-icons/bi";
import { CiMoneyBill } from "react-icons/ci";
import { GoHome } from "react-icons/go";
import AppUserMenu from "../components/AppUserMenu";
import HeaderClockAction from "../components/HeaderClockAction";

import CRMLogo from "../assets/icons/CRM_logo.svg";
import TicketsLogo from "../assets/icons/Tickets_logo.svg";
import AdminLogo from "../assets/icons/Admin_logo.svg";
const crmNavItems = [
	{ id: "dashboard", label: "Dashboard", icon: <IoIosStats size={24} /> },
	{ id: "kanban", label: "Kanban", icon: <TbLayoutDashboard size={24} /> },
	{ id: "contacts", label: "Contacts", icon: <FiUsers size={24} /> },
	{ id: "accounts", label: "Accounts", icon: <FiBriefcase size={24} /> },
	{ id: "conversation", label: "Conversation", icon: <BiMessageSquareDots size={24} /> },
	{ id: "deals", label: "Deals", icon: <CiMoneyBill size={24} /> },
];

const CRM_NAV_STATE_KEY = "crm_nav_state";

const CRM_PAGE_HEADER_TITLES = {
	Dashboard: "Dashboard",
	Contacts: "Contacts",
	Accounts: "Accounts",
	Kanban: "Kanban",
	Conversation: "Conversations",
	Deals: "Deals",
};

function loadNavState() {
	try {
		const rawState = window.sessionStorage.getItem(CRM_NAV_STATE_KEY);
		if (!rawState) {
			return null;
		}

		const parsedState = JSON.parse(rawState);
		if (!parsedState || typeof parsedState !== "object") {
			return null;
		}

		return parsedState;
	} catch {
		return null;
	}
}

function CRM_main() {
	const appLinks = useMemo(() => ([
        { label: "CRM", image: CRMLogo, href: "/contacts", variant: "crm" },
        { label: "Tickets", image: TicketsLogo, href: "/IT", variant: "tickets" },
        { label: "Admin", image: AdminLogo, href: "/admin", variant: "admin" },
        { label: "Projects", image: AdminLogo, href: "/projects", variant: "projects" },
        { label: "Payment", image: AdminLogo, href: "/payment", variant: "payment" },
    ]), []);

	const savedNavState = typeof window !== "undefined" ? loadNavState() : null;
	const [activePage, setActivePage] = useState(savedNavState?.activePage || "Dashboard");
	const [selectedContactId, setSelectedContactId] = useState(savedNavState?.selectedContactId || null);
	const [selectedContactPreview, setSelectedContactPreview] = useState(savedNavState?.selectedContactPreview || null);
	const [contactDetailReturnPage, setContactDetailReturnPage] = useState(savedNavState?.contactDetailReturnPage || "Contacts");
	const [contacts, setContacts] = useState([]);
	const [loadingContacts, setLoadingContacts] = useState(false);
	const [followUps, setFollowUps] = useState([]);
	const [loadingFollowUps, setLoadingFollowUps] = useState(false);
	const [followUpsError, setFollowUpsError] = useState("");
	const profileName = useAuthStore((state) => state.profile?.name ?? "User");
	const profileId = useAuthStore((state) => state.profile?.id ?? null);
	const hydrateDeals = useDealStore((state) => state.hydrateDeals);
	const setFollowUpsLookup = useAppDataStore((state) => state.setFollowUpsLookup);
	const setFollowUpsForContact = useAppDataStore((state) => state.setFollowUpsForContact);
  
	const pendingContactNav = useAppDataStore((state) => state.pendingContactNav);
	const clearContactNav = useAppDataStore((state) => state.clearContactNav);
  
	const [pageHeader, setPageHeader] = useState({
		title: CRM_PAGE_HEADER_TITLES.Dashboard,
		back: false,
		onBack: null,
		children: null,
	});

	const handleFollowUpsForContactUpdate = useCallback((contactId, rows) => {
		setFollowUpsForContact(contactId, rows);
	}, [setFollowUpsForContact]);

	async function loadContacts() {
		setLoadingContacts(true);
		try {
			const data = await getContacts();
			setContacts(data);
		} catch (err) {
			console.error("Failed to load contacts", err);
			setContacts([]);
		} finally {
			setLoadingContacts(false);
		}
	}

	useEffect(() => {
		loadContacts();
	}, []);

	async function loadFollowUps() {
		setLoadingFollowUps(true);
		setFollowUpsError("");
		try {
			const data = await getFollowUps();
			const list = Array.isArray(data) ? data : [];
			setFollowUps(list);
			setFollowUpsLookup(list);
		} catch (err) {
			console.error("Failed to load follow ups", err);
			setFollowUps([]);
			setFollowUpsLookup([]);
			setFollowUpsError(err?.message || "Failed to load follow ups");
		} finally {
			setLoadingFollowUps(false);
		}
	}

	useEffect(() => {
		loadFollowUps();
	}, []);

	useEffect(() => {
		if (!profileId) {
			return;
		}

		hydrateDeals();
	}, [profileId, hydrateDeals]);

	useEffect(() => {
		try {
			window.sessionStorage.setItem(
				CRM_NAV_STATE_KEY,
				JSON.stringify({
					activePage,
					selectedContactId,
					selectedContactPreview,
					contactDetailReturnPage,
				})
			);
		} catch {
			// Ignore storage errors and fall back to in-memory state.
		}
	}, [activePage, selectedContactId, selectedContactPreview, contactDetailReturnPage]);

	useEffect(() => {
		if(!pendingContactNav){
			return;
		}
		setContactDetailReturnPage(activePage);
		setSelectedContactId(pendingContactNav.id || null);
		setSelectedContactPreview(pendingContactNav);
		setActivePage("ContactDetail");
		clearContactNav();
	}, [pendingContactNav])

	function handleContactSelect(contact) {
		setContactDetailReturnPage(activePage);
		setSelectedContactId(contact?.id || null);
		setSelectedContactPreview(contact || null);
		setActivePage("ContactDetail");
	}

	function handleBackFromContact() {
		setSelectedContactId(null);
		setSelectedContactPreview(null);
		setActivePage(contactDetailReturnPage || "Contacts");
	}

	useEffect(() => {
		if (activePage === "ContactDetail") {
			const fallbackName = [
				selectedContactPreview?.first_name,
				selectedContactPreview?.last_name,
			]
				.filter(Boolean)
				.join(" ")
				.trim();

			setPageHeader({
				title: selectedContactPreview?.name || fallbackName || "Contact",
				back: true,
				onBack: handleBackFromContact,
				children: null,
			});
			return;
		}

		if (activePage === "Dashboard") {
			return;
		}

		setPageHeader({
			title: CRM_PAGE_HEADER_TITLES[activePage] || CRM_PAGE_HEADER_TITLES.Dashboard,
			back: false,
			onBack: null,
			children: null,
		});
	}, [activePage, selectedContactPreview]);

	const renderPage = () => {
		switch (activePage) {
			case "Dashboard":
				return (
					<Dashboard
						setPageHeader={setPageHeader}
						onContactSelect={handleContactSelect}
						contacts={contacts}
						loadingContacts={loadingContacts}
						onContactsRefresh={loadContacts}
						followUps={followUps}
						loadingFollowUps={loadingFollowUps}
						followUpsError={followUpsError}
						onFollowUpsRefresh={loadFollowUps}
					/>
				);
			case "Contacts":
				return (
					<Contacts
						onContactSelect={handleContactSelect}
						contacts={contacts}
						loadingContacts={loadingContacts}
						onContactsRefresh={loadContacts}
						followUps={followUps}
						onFollowUpsRefresh={loadFollowUps}
					/>
				);
			case "Accounts":
				return <Accounts onContactSelect={handleContactSelect} />;
			case "ContactDetail":
				return (
					<ContactDetail
						setPageHeader={setPageHeader}
						contactId={selectedContactId}
						initialContact={selectedContactPreview}
						onBack={handleBackFromContact}
						followUps={followUps}
						followUpsLoading={loadingFollowUps}
						followUpsError={followUpsError}
						onFollowUpsRefresh={loadFollowUps}
						onFollowUpsForContactUpdate={handleFollowUpsForContactUpdate}
					/>
				);
			case "Kanban":
				return <Kanban />;
			case "Conversation":
				return <Conversation />;
			case "Deals":
				return <Deals />;
			default:
				return (
					<Dashboard
						setPageHeader={setPageHeader}
						onContactSelect={handleContactSelect}
						contacts={contacts}
						loadingContacts={loadingContacts}
						onContactsRefresh={loadContacts}
						followUps={followUps}
						loadingFollowUps={loadingFollowUps}
						followUpsError={followUpsError}
						onFollowUpsRefresh={loadFollowUps}
					/>
				);
		}
	};

	return (
		<div className="crm-layout app">
			<div className="nav-section">
				<NavBar
					activePage={activePage}
					navItems={crmNavItems}
					onPageChange={(page) => {
						setSelectedContactId(null);
						setSelectedContactPreview(null);
						setContactDetailReturnPage(page);
						setActivePage(page);
					}}
					footerAction={{
						label: "Home",
						icon: <GoHome size={24} />,
						onClick: () => {
							window.location.href = "/";
						},
					}}
				/>
			</div>
			<div className="page-logo-container">
				<img className="nav-logo" src={CRMLogo} alt="CRM Logo" />
			</div>
			<PageHeaderProvider userMenu={(
				<>
					<HeaderClockAction />
					<AppSwitcher apps={appLinks} activeLabel="Home" />
					<AppUserMenu name={profileName} id={profileId} />
				</>
			)}
			>
				<PageHeader title={pageHeader.title} back={pageHeader.back} onBack={pageHeader.onBack}>
					{pageHeader.children}
				</PageHeader>
				{renderPage()}
			</PageHeaderProvider>
		</div>
	);
}

export default CRM_main;